/**
 * SBS (Side-by-Side) evaluation types for UI components.
 *
 * These types are used by the SBS consent modal and feedback bar
 * rendered inside the ConversationAssistant component tree.
 */

// ── Event name constants ──────────────────────────────────────────────

/** The custom event name dispatched by useSBSTrigger in the platform package. */
export const SBS_TRIGGER_EVENT_NAME = 'rovo-sbs-trigger';

/** The custom event name fired when the user responds to consent (listened by useSBSTrigger). */
export const SBS_CONSENT_RECEIVED_EVENT_NAME = 'rovo-sbs-consent-received';

/** The source identifier included in all postMessage payloads so the content script bridge can identify SBS messages. */
export const SBS_BRIDGE_SOURCE = 'rovo-sbs-bridge';

// ── Type aliases ──────────────────────────────────────────────────────

/** The type of third-party competitor being compared against Rovo. */
export type CompetitorType = 'chatgpt' | 'gemini';

// ── Shared interfaces ─────────────────────────────────────────────────

export interface SBSTriggerEventDetail {
	query: string;
	chatMode: string;
	knowledgeSources: string[];
	rovoEnabledConnectors: string[];
	conversationId: string;
	siteId: string;
	product: string;
	/** Whether the user passes the rovo-ext_sbs_chatgpt_eligible gate. */
	isChatGPTEligible: boolean;
}

/** Response data for a single side (Rovo or competitor) in an SBS evaluation. */
export interface SBSResponseData {
	text: string;
	screenshotUrl: string;
	latencyMs: number;
}

export interface SBSEvaluationData {
	evaluationId: string;
	query: string;
	competitorType: CompetitorType;
	status: string;
	/** The product context in which the evaluation was triggered (e.g. 'rovo', 'confluence'). */
	product: string;
	bridgeSessionId?: string;
	/** Whether the competitor response has finished streaming. */
	competitorResponseReady: boolean;
	/** Whether the Rovo response has finished streaming in the side panel. */
	rovoResponseReady: boolean;
	/** Whether the competitor page is loaded and ready to accept a query. */
	competitorPageReady?: boolean;
	/** Whether the Rovo side panel is mounted and ready to accept a query. */
	rovoPanelReady?: boolean;
	/** The type of error that caused the evaluation to fail. */
	errorType?: string;
	/** A human-readable error message associated with the error. */
	errorMessage?: string;
	rovoResponse: SBSResponseData;
	competitorResponse: SBSResponseData;
}

/** A five-point preference scale for comparing Rovo against a competitor. */
export type SBSPreference =
	| 'rovo_much_better'
	| 'rovo_slightly_better'
	| 'about_the_same'
	| 'competitor_slightly_better'
	| 'competitor_much_better';

/** The user's response to the SBS consent prompt. */
export type SBSConsentResponse = 'participate' | 'decline' | 'opt_out';

/** Predefined reason tags for explaining a preference. */
export const SBS_REASON_TAGS = [
	'better_grounding_information',
	'better_formatted_response',
	'answer_length_more_adequate',
	'followed_instructions_better',
	'more_relevant_to_me',
	'it_was_faster',
	'less_hallucination',
] as const;

/** A single reason tag value from the predefined set. */
export type SBSReasonTag = (typeof SBS_REASON_TAGS)[number];

/** The feedback data submitted by the user. */
export interface SBSFeedbackSubmission {
	/** The user's preference rating. */
	preference: SBSPreference;
	/** Selected reason tags explaining the preference. */
	reasons: SBSReasonTag[];
	/** Optional free-text feedback from the user. */
	freeTextFeedback?: string;
}
